import {
  BooleanFilter,
  DateFilterType,
  FetchItemCacheKey,
  FetchItemFetched,
  FetchItemGqlQuery,
  FetchItemIsPlaceholderData,
  FetchItemsCacheKey,
  FetchItemsClearItemsFilters,
  FetchItemsEditItem,
  FetchItemsErrorMessage,
  FetchItemsFetched,
  FetchItemsFilterItems,
  FetchItemsGqlQuery,
  FetchItemsIsPlaceholderData,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsPaginateItems,
  FetchItemsPrefetchItems,
  FetchItemsShowItem,
  FetchItemsSort,
  FetchItemsSortItems,
  FetchItemsTotalCount,
  IdFilterType,
  NanoIDFilter,
  NanoIdFilterType,
  NumberFilterType,
  PositiveIdFilterType,
  PositiveTextFilterType,
  StringFilter,
  TextFilterType,
  UUIDFilter
} from '../../../types';
import { TagID } from '../../tags/tagsTypes';
import {
  Checked,
  CheckedAll,
  CheckedHashItem,
  OnCheckAll,
  OnSetCheckedIds
} from '../../../common/hooks/useTableCheckable';
import {
  TaskBurning,
  TaskFavorite,
  TaskMemberIdsFilter,
  TaskPerformerIdsFilter,
  TaskStatuses
} from './tasksFields';

import { UpdateIndexQueryItemCacheAction } from '../../common/reactQuery/hooks/useIndexQuery';
import { UserNanoID } from '../../users/usersTypes';
import { TeamNanoID } from '../../teams/teamsTypes';

export interface TasksSimilarImageFilter {
  imageUrl: string;
  limit?: number;
}

export interface FetchTasksFilters {
  amountCents?: NumberFilterType;
  badDebt?: BooleanFilter;
  burning?: TaskBurning;
  createdAt?: DateFilterType;
  debtUserNanoId?: NanoIDFilter;
  debtUserUuid?: UUIDFilter;
  favorite?: TaskFavorite;
  id?: IdFilterType;
  implementationDate?: DateFilterType;
  lastActivity?: DateFilterType;
  memberFullName?: StringFilter;
  memberIds?: TaskMemberIdsFilter;
  memberNanoIds?: UserNanoID[];
  name?: TextFilterType;
  notes?: TextFilterType;
  performerIds?: TaskPerformerIdsFilter;
  plannedStartDate?: DateFilterType;
  projectNanoId?: NanoIDFilter;
  projectNanoIds?: NanoIDFilter[];
  projectUuid?: UUIDFilter;
  projectId?: IdFilterType;
  resultsUploadedAt?: DateFilterType;
  similarImage?: TasksSimilarImageFilter;
  sku?: TextFilterType;
  sourceFilesCount?: NumberFilterType;
  startDate?: DateFilterType;
  status?: TextFilterType;
  statusUpdatedAt?: DateFilterType;
  tagIds?: TagID[];
  teamNanoIds?: TeamNanoID[];
  visibleForClient?: BooleanFilter;
  visibleResultsCount?: NumberFilterType;
  withoutSourceFiles?: BooleanFilter;
  // only fin tasks
  itemTypeName?: PositiveTextFilterType;
  ownerId?: IdFilterType;
}

export interface FetchFinTasksFilters {
  burning?: TaskBurning;
  createdAt?: DateFilterType;
  favorite?: TaskFavorite;
  implementationDate?: DateFilterType;
  itemId?: PositiveIdFilterType;
  itemTypeName?: PositiveTextFilterType;
  lastActivity?: DateFilterType;
  notes?: TextFilterType;
  sku?: TextFilterType;
  ownerId?: IdFilterType;
  performerId?: IdFilterType;
  projectNanoId?: NanoIdFilterType;
  status?: TextFilterType;
  tagId?: IdFilterType;
  teamNanoId?: NanoIdFilterType;
  visibleForClient?: BooleanFilter;
  visibleResultsCount?: NumberFilterType;
}

export type FetchTasksFiltersFunc = (nextFilters: FetchTasksFilters) => void;
export type FetchTasksFilterTasks = FetchItemsFilterItems<FetchTasksFilters>;
export type FetchFinTasksFilterTasks =
  FetchItemsFilterItems<FetchFinTasksFilters>;
export type FetchTasksClearTasksFilters = FetchItemsClearItemsFilters;

export enum FetchTasksSortableFields {
  ID = 'id',
  NAME = 'name',
  IMPLEMENTATION_DATE = 'implementation_date',
  PLANNED_START_DATE = 'planned_start_date',
  STATUS = 'status',
  STATUS_NUMBER = 'status_number',
  AMOUNT = 'amount_cents',
  PREPAYMENT = 'prepayment_cents',
  RESULTS_UPLOADED_AT = 'results_uploaded_at',
  CREATED_AT = 'createdAt',
  LAST_ACTIVITY = 'last_activity',
  START_DATE = 'start_date',
  STATUS_UPDATED_AT = 'status_updated_at'
}

export enum FetchTasksFilterFields {
  AMOUNT_CENTS = 'amountCents',
  BURNING = 'burning',
  CREATED_AT = 'createdAt',
  FAVORITE = 'favorite',
  IMPLEMENTATION_DATE = 'implementationDate',
  ITEM_TYPE_NAME = 'itemTypeName',
  LAST_ACTIVITY = 'lastActivity',
  MEMBER_IDS = 'memberIds',
  NAME = 'name',
  NOTES = 'notes',
  PERFORMER_IDS = 'performerIds',
  PROJECT_NANO_ID = 'projectNanoId',
  SIMILAR_IMAGE = 'similarImage',
  SKU = 'sku',
  SOURCE_FILES_COUNT = 'sourceFilesCount',
  START_DATE = 'startDate',
  STATUS = 'status',
  STATUS_EQ = 'status.eq',
  STATUS_IN = 'status.in',
  STATUS_NOT_EQ = 'status.notEq',
  STATUS_UPDATED_AT = 'statusUpdatedAt',
  TAG_IDS = 'tagIds',
  TEAM_NANO_IDS = 'teamNanoIds',
  VISIBLE_RESULTS_COUNT = 'visibleResultsCount'
}

export enum FetchTasksSortTypes {
  ID_ASC = 'ID_ASC',
  ID_DESC = 'ID_DESC',
  NAME_ASC = 'NAME_ASC',
  NAME_DESC = 'NAME_DESC',
  IMPLEMENTATION_DATE_ASC = 'IMPLEMENTATION_DATE_ASC',
  IMPLEMENTATION_DATE_DESC = 'IMPLEMENTATION_DATE_DESC',
  STATUS_ASC = 'STATUS_ASC',
  STATUS_DESC = 'STATUS_DESC',
  STATUS_NUMBER_ASC = 'STATUS_NUMBER_ASC',
  STATUS_NUMBER_DESC = 'STATUS_NUMBER_DESC',
  STATUS_NUMBER_ASC_ASC = 'STATUS_NUMBER_ASC_ASC',
  STATUS_NUMBER_DESC_DESC = 'STATUS_NUMBER_DESC_DESC',
  START_DATE_ASC = 'START_DATE_ASC',
  START_DATE_DESC = 'START_DATE_DESC',
  AMOUNT_ASC = 'AMOUNT_ASC',
  AMOUNT_DESC = 'AMOUNT_DESC',
  PREPAYMENT_ASC = 'PREPAYMENT_ASC',
  PREPAYMENT_DESC = 'PREPAYMENT_DESC',
  CREATED_AT_ASC = 'CREATED_AT_ASC',
  CREATED_AT_DESC = 'CREATED_AT_DESC',
  RESULTS_UPLOADED_AT_ASC = 'RESULTS_UPLOADED_AT_ASC',
  RESULTS_UPLOADED_AT_DESC = 'RESULTS_UPLOADED_AT_DESC',
  SIMILARITY_SCORE_DESC = 'SIMILARITY_SCORE_DESC',
  LAST_ACTIVITY_ASC = 'LAST_ACTIVITY_ASC',
  LAST_ACTIVITY_DESC = 'LAST_ACTIVITY_DESC'
}

export type TaskStatusFilter = {
  eq?: TaskStatuses;
  in?: TaskStatuses[];
  notEq?: TaskStatuses;
  notIn?: TaskStatuses[];
};

export type FetchTasksGqlQuery = FetchItemsGqlQuery;
export type FetchTaskGqlQuery = FetchItemGqlQuery;
export type FetchTasksCacheKey = FetchItemsCacheKey;
export type FetchTasksCacheKeys = FetchTasksCacheKey[];
export type FetchTaskCacheKey = FetchItemCacheKey;
export type FetchToggleTaskFavoriteCacheKey = FetchItemCacheKey;

export type FetchTasksFetched = FetchItemsFetched;
export type FetchTasksErrorMessage = FetchItemsErrorMessage;
export type FetchTasksIsPlaceholderData = FetchItemsIsPlaceholderData;
export type FetchTasksPage = FetchItemsPage;
export type FetchTasksLimit = FetchItemsLimit;
export type FetchTasksSort = FetchItemsSort; // FetchTasksSortTypes[];
export type FetchTasksTotalCount = FetchItemsTotalCount;
export type FetchTasksSortTasks = FetchItemsSortItems;
export type FetchTasksPaginateTasks = FetchItemsPaginateItems;
export type FetchTasksPrefetchTasks = FetchItemsPrefetchItems;
export type FetchTasksShowTask = FetchItemsShowItem;
export type FetchTasksEditTask = FetchItemsEditItem;

export type FetchTaskFetched = FetchItemFetched;
export type FetchTaskIsPlaceholderData = FetchItemIsPlaceholderData;

export type FetchTasksChecked = Checked;
export type FetchTasksCheckedHash = CheckedHashItem;
export type FetchTasksOnSetCheckedIds = OnSetCheckedIds;
export type FetchTasksCheckedAll = CheckedAll;
export type FetchTasksOnCheckAll = OnCheckAll;

export type UpdatePaginatedTasksItemCacheAction<ItemType> =
  UpdateIndexQueryItemCacheAction<ItemType>;
